/**
 * BuscadorCobro.jsx — encontrar UN cobro por su id del PSP, sin filtro de fecha.
 *
 * PARA QUÉ ES ESTO
 * ----------------
 * `ConciliacionDelDia` cruza un día completo; esta pantalla resuelve el caso
 * contrario: soporte recibe «me cobraron y no me aparece» con un solo dato en
 * la mano —el id que el PSP le mostró al cliente—, y no necesariamente sabe
 * (o el cliente no sabe decir) de qué día fue. Buscar por fecha primero sería
 * pedirle a soporte que adivine el filtro antes de poder mirar el cobro.
 *
 * Por eso no recibe `fecha` ni `psp`: es un formulario independiente, con su
 * propia carga y su propio token, igual que `ConciliacionDelDia` (mismo
 * patrón, spec §10.4.2) pero sin nada que dependa del día que se esté viendo.
 *
 * DOS ESTADOS DISTINTOS, NO UNO
 * ------------------------------
 * «No encontrado» (`encontrado: false`) es una respuesta VÁLIDA del sistema — pero no
 * necesariamente del PSP. `reconsulta-liquidacion.service.ts` devuelve ese mismo valor
 * por CUATRO motivos distintos, y en la mayoría de ellos el PSP ni se llega a consultar:
 * no hay ningún cargo local con ese id, al cargo le faltan `external_charge_id` o
 * `payment_provider`, o no hay adapter registrado para su proveedor — se detecta ANTES de
 * hablar con nadie. Sólo en el cuarto caso el sistema sí le preguntó al PSP y el PSP
 * contestó que no tiene esa transacción. Un error real (la llamada se cae, `payment`
 * responde con un error) es una tercera cosa —algo salió mal antes de poder responder la
 * pregunta—. Mezclar cualquiera de los dos primeros con «el PSP dijo que no» le haría
 * creer a soporte algo que el sistema no puede afirmar.
 *
 * UN HUECO YA CONOCIDO, NO DE ESTE COMPONENTE. `GET /api/reconsulta-cobro`
 * siempre consulta al PSP con `tipo: 'subscription'`; si el cobro encontrado
 * es en realidad `one_time`, la consulta al PSP usa el método equivocado. Es
 * un gap ya identificado del lado de `payment` — nada que este formulario
 * pueda corregir desde el cliente.
 *
 * @see src/services/yoolpay/conciliacionApi.js — buscarCobro, mismo endpoint reconsulta-cobro
 * @see src/components/payments/yoolpay/ConciliacionDelDia.jsx — patrón hermano (token propio, Aviso por severidad)
 */
import React, { useCallback, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { buscarCobro } from '../../../services/yoolpay/conciliacionApi';
import { Aviso, Rotulo, Seccion } from './cierre/piezas';
import { Boton, Input } from './cierre/ds';

export default function BuscadorCobro() {
  const { getToken } = useAuth();

  const [id, setId] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState(null);
  /**
   * `null` = todavía no se buscó nada. Una vez que vuelve la respuesta del
   * PSP es el payload tal cual lo devuelve `buscarCobro`
   * (`{ encontrado, chargeId?, cambios? }`) — nunca se reescribe a mano.
   */
  const [resultado, setResultado] = useState(null);

  const buscar = useCallback(async () => {
    const externalChargeId = id.trim();
    if (!externalChargeId || buscando) return;
    setBuscando(true);
    setError(null);
    setResultado(null);
    try {
      const token = await getToken();
      setResultado(await buscarCobro(token, externalChargeId));
    } catch (e) {
      setError(e?.message ?? 'No se pudo reconsultar el cobro con el PSP');
    } finally {
      setBuscando(false);
    }
  }, [getToken, id, buscando]);

  return (
    <Seccion
      titulo="Buscador de cobro"
      bajada={
        'Para el caso de soporte: el cliente dice que le cobraron y no aparece. Se busca '
        + 'por el id que el PSP le mostró a él, sin importar el día ni si ya se conciliaría '
        + 'en la corrida diaria.'
      }
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', padding: '18px 24px' }}>
        <div style={{ flex: 1, maxWidth: 420 }}>
          <Rotulo>External charge id (PSP)</Rotulo>
          <div style={{ marginTop: 6 }}>
            <Input
              value={id}
              onChange={(e) => setId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
              placeholder="Ej: ch_3P..."
              disabled={buscando}
              aria-label="External charge id (PSP)"
            />
          </div>
        </div>
        <Boton onClick={buscar} disabled={buscando || !id.trim()}>
          {buscando ? 'Buscando…' : 'Buscar'}
        </Boton>
      </div>

      {error ? (
        <div style={{ padding: '0 24px 20px' }}>
          <Aviso tono="alta" titulo="No se pudo reconsultar el cobro.">
            {error} — es un error de la llamada, no una respuesta del PSP: no se sabe todavía
            si el cobro existe o no.
          </Aviso>
        </div>
      ) : null}

      {!error && resultado && !resultado.encontrado ? (
        <div style={{ padding: '0 24px 20px' }}>
          <Aviso tono="baja" titulo="No se encontró el cobro.">
            Puede ser que no exista ningún cargo local con ese id —dato para escalar, el PSP
            ni se llegó a consultar— o que sí exista y el PSP no tenga esa transacción. Desde
            aquí no se puede distinguir cuál de los dos fue; no es, por sí solo, evidencia de
            un cobro perdido.
          </Aviso>
        </div>
      ) : null}

      {!error && resultado?.encontrado ? (
        <div style={{ padding: '0 24px 24px', display: 'grid', gap: 12 }}>
          <Aviso tono="ok" titulo="Cobro encontrado.">
            Cargo local: {resultado.chargeId ?? '— (el PSP lo tiene, pero sin cargo local asociado)'}
          </Aviso>
          <div>
            <Rotulo>Datos actualizados del PSP</Rotulo>
            <pre
              style={{
                marginTop: 6,
                padding: 14,
                background: '#FAFAF9',
                border: '1px solid var(--yool-border)',
                borderRadius: 12,
                fontSize: 12,
                lineHeight: 1.5,
                overflow: 'auto',
                maxHeight: 420,
              }}
            >
              {JSON.stringify(resultado.cambios ?? {}, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}
    </Seccion>
  );
}
